import { isActivelySnoozed, type TaskStatus } from '@erp/shared-types';

/**
 * What sending a task back to its task type's default agent will change.
 * The task page lists these in a confirmation dialog before calling
 * `tasks.sendBackToAgent`. Null means there is nothing to send: the type
 * has no default agent, or the task is already pending with that agent.
 */
export type SendBackAction =
  | { kind: 'assign' }
  | { kind: 'set_pending'; fromStatus: TaskStatus }
  | { kind: 'clear_outcome' }
  | { kind: 'release_run' }
  | { kind: 'clear_snooze' };

export interface SendBackPlan {
  agentId: string;
  actions: SendBackAction[];
}

export interface SendBackTaskSnapshot {
  status: TaskStatus;
  assigneeType: 'user' | 'agent' | null;
  assigneeAgentDefinitionId: string | null;
  outcome: string | null;
  outcomeReason: string | null;
  snoozedUntil?: Date | string | null;
}

export interface SendBackTaskTypeSnapshot {
  defaultAssigneeType: string | null;
  defaultAssigneeAgentDefinitionId: string | null;
}

function typeAgentId(
  taskType: SendBackTaskTypeSnapshot | null | undefined,
): string | null {
  if (taskType?.defaultAssigneeType !== 'agent') return null;
  return taskType.defaultAssigneeAgentDefinitionId;
}

function assignedToAgent(task: SendBackTaskSnapshot, agentId: string): boolean {
  return (
    task.assigneeType === 'agent' && task.assigneeAgentDefinitionId === agentId
  );
}

export function planSendBackToTypeAgent(input: {
  task: SendBackTaskSnapshot;
  taskType: SendBackTaskTypeSnapshot | null | undefined;
  now?: Date;
}): SendBackPlan | null {
  const agentId = typeAgentId(input.taskType);
  if (!agentId) return null;

  const { task } = input;
  const sameAgent = assignedToAgent(task, agentId);
  if (task.status === 'pending' && sameAgent) return null;

  const actions: SendBackAction[] = [];
  if (!sameAgent) actions.push({ kind: 'assign' });
  if (task.status !== 'pending') {
    actions.push({ kind: 'set_pending', fromStatus: task.status });
  }
  if (
    task.status !== 'pending' &&
    (task.outcome != null || task.outcomeReason != null)
  ) {
    actions.push({ kind: 'clear_outcome' });
  }
  if (task.status === 'in_progress') actions.push({ kind: 'release_run' });
  if (
    !sameAgent &&
    isActivelySnoozed(task.snoozedUntil, input.now ?? new Date())
  ) {
    actions.push({ kind: 'clear_snooze' });
  }
  return { agentId, actions };
}
